// src/components/BulkEditModal.jsx
import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Alert
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import creFields from '../data/creFields.json';
import { getChoiceDisplayLabel } from '../utils/creUtils';

dayjs.extend(utc);
dayjs.extend(timezone);

const BulkEditModal = ({ open, onClose, selectedRows, onSave }) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [fieldValues, setFieldValues] = useState({});

  // Get editable fields (exclude system fields and non-editable fields)
  const editableFields = Object.entries(creFields)
    .filter(([key, config]) => !['MODIFIED_BY', 'MODIFIED_DATE', 'EDIT', 'CUSIP'].includes(key) && config.editable !== false)
    .map(([key, config]) => ({
      key,
      label: config.name,
      datatype: config.datatype,
      choices: config.choices,
    }));

  const handleFieldChange = (field, value) => {
    setFieldValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApply = () => {
    if (selectedFields.length === 0) {
      return;
    }

    // Create update object with only selected fields
    const updates = {};
    selectedFields.forEach(field => {
      if (fieldValues[field] !== undefined) {
        updates[field] = fieldValues[field];
      }
    });

    // Apply updates to all selected rows
    // MODIFIED_DATE is the loaded version token, not an edit timestamp. Keep
    // it untouched so the writer rejects a stale bulk save with 409.
    const updatedRows = selectedRows.map(row => ({ ...row, ...updates }));

    onSave(updatedRows);
    handleClose();
  };

  const handleClose = () => {
    setSelectedFields([]);
    setFieldValues({});
    onClose();
  };

  const renderFieldInput = (fieldKey) => {
    const field = editableFields.find(f => f.key === fieldKey);
    if (!field) return null;

    const value = fieldValues[fieldKey] !== undefined ? fieldValues[fieldKey] : '';

    // Date field
    if (field.datatype === 'date') {
      return (
        <DatePicker
          label={field.label}
          value={value ? dayjs(value) : null}
          onChange={(newValue) => handleFieldChange(fieldKey, newValue)}
          format="YYYY-MM-DD"
          slotProps={{
            textField: {
              fullWidth: true,
              size: 'small'
            }
          }}
        />
      );
    }

    // Boolean field
    if (field.datatype === 'boolean') {
      return (
        <FormControl fullWidth size="small" sx={{ minWidth: `${field.label.length + 4}ch` }}>
          <InputLabel sx={{ maxWidth: 'none' }}>{field.label}</InputLabel>
          <Select
            value={value === true ? 'true' : value === false ? 'false' : ''}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value === 'true')}
            label={field.label}
          >
            <MenuItem value="true">Yes</MenuItem>
            <MenuItem value="false">No</MenuItem>
          </Select>
        </FormControl>
      );
    }

    // Dropdown field with choices
    if (field.choices && Array.isArray(field.choices)) {
      return (
        <FormControl fullWidth size="small" sx={{ minWidth: `${field.label.length + 4}ch` }}>
          <InputLabel sx={{ maxWidth: 'none' }}>{field.label}</InputLabel>
          <Select
            value={value}
            onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
            label={field.label}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {field.choices.map((choice) => (
              <MenuItem key={choice} value={choice}>
                {getChoiceDisplayLabel(fieldKey, choice)}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      );
    }

    // Number field
    if (field.datatype === 'number') {
      return (
        <TextField
          fullWidth
          size="small"
          label={field.label}
          type="number"
          value={value}
          onChange={(e) => handleFieldChange(fieldKey, parseFloat(e.target.value))}
        />
      );
    }

    // Text field (default)
    return (
      <TextField
        fullWidth
        size="small"
        label={field.label}
        value={value}
        onChange={(e) => handleFieldChange(fieldKey, e.target.value)}
      />
    );
  };

  const getPreviewValue = (row, fieldKey) => {
    const oldValue = row[fieldKey];
    const newValue = fieldValues[fieldKey];

    if (newValue === undefined) return oldValue;

    // Format dates
    if (typeof newValue === 'object' && newValue?.format) {
      return newValue.format('YYYY-MM-DD');
    }

    // Format booleans
    if (typeof newValue === 'boolean') {
      return newValue ? 'Y' : 'N';
    }

    return String(newValue);
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Bulk Edit {selectedRows.length} Loan{selectedRows.length > 1 ? 's' : ''}
            </Typography>
            <IconButton onClick={handleClose} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {/* Field Selector */}
          <Box sx={{ mb: 3 }}>
            <Typography variant="subtitle2" gutterBottom>
              Select Fields to Update
            </Typography>
            <Autocomplete
              multiple
              options={editableFields}
              getOptionLabel={(option) => option.label}
              value={editableFields.filter(f => selectedFields.includes(f.key))}
              onChange={(event, newValue) => {
                setSelectedFields(newValue.map(v => v.key));
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder="Choose fields to edit..."
                  size="small"
                />
              )}
              renderTags={(value, getTagProps) =>
                value.map((option, index) => (
                  <Chip
                    label={option.label}
                    {...getTagProps({ index })}
                    size="small"
                  />
                ))
              }
            />
          </Box>

          {/* Field Value Inputs */}
          {selectedFields.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Enter New Values
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {selectedFields.map(fieldKey => (
                  <Box key={fieldKey}>
                    {renderFieldInput(fieldKey)}
                  </Box>
                ))}
              </Box>
            </Box>
          )}

          {/* Preview */}
          {selectedFields.length > 0 && (
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preview Changes (First 5 Rows)
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                These changes will be applied to all {selectedRows.length} selected loan(s)
              </Alert>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>CUSIP</TableCell>
                      {selectedFields.map(fieldKey => {
                        const field = editableFields.find(f => f.key === fieldKey);
                        return (
                          <TableCell key={fieldKey}>
                            {field?.label}
                          </TableCell>
                        );
                      })}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedRows.slice(0, 5).map((row) => (
                      <TableRow key={row.CUSIP}>
                        <TableCell>{row.CUSIP}</TableCell>
                        {selectedFields.map(fieldKey => (
                          <TableCell key={fieldKey}>
                            <Box>
                              <Typography
                                variant="body2"
                                sx={{ textDecoration: 'line-through', color: 'text.secondary' }}
                              >
                                {row[fieldKey] || '(empty)'}
                              </Typography>
                              <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                                → {getPreviewValue(row, fieldKey)}
                              </Typography>
                            </Box>
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                    {selectedRows.length > 5 && (
                      <TableRow>
                        <TableCell colSpan={selectedFields.length + 1} align="center">
                          <Typography variant="body2" color="text.secondary">
                            ... and {selectedRows.length - 5} more row(s)
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {selectedFields.length === 0 && (
            <Alert severity="info">
              Select fields above to begin bulk editing
            </Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            color="primary"
            disabled={selectedFields.length === 0}
          >
            Apply to {selectedRows.length} Loan{selectedRows.length > 1 ? 's' : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BulkEditModal;
